import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody, CardHeader, CircularProgress } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { BsDatabaseFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { TfiArrowTopRight } from "react-icons/tfi";

import GenericTable from "../../../components/Kit/GenericTable";
import ACircleUserDisplay from "../../../components/Kit/ACircleUserDisplay";
import AStarGradeDisplay from "../../../components/Kit/AStarGradeDisplay";

import { dashboard_table_columns } from "../../../constants/tableHeaders";
import { getTopFlopOperators } from "../../../utils/operatorUtils";
import { capitalizeFirstLetter } from "../../../utils/stringUtils";

// The department and direction filters are applied server-side, so the
// operators handed down here are already scoped to the current selection.
const TopFlopTable = () => {
  const { t } = useTranslation();
  const { operators, loading_operators } = useSelector((state) => state.analytics.analytics);
  const [topFlopCalls, setTopFlopCalls] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (operators && operators?.length >= 0) {
      setTopFlopCalls(getTopFlopOperators(operators));
    }
  }, [operators]);

  const onOperatorClick = (item) => () => {
    navigate(`/representatives/${item.id}`);
  };

  const renderCardHeader = () => {
    return (
      <div className="w-full flex items-center justify-between gap-x-4">
        <h6 className="text-[24px] font-semibold text-primary">{t("Top/Flop")}</h6>
      </div>
    );
  };

  const tableEmptyContent = () => {
    return (
      <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
        <BsDatabaseFill className="text-gray-300 w-12 h-12" />
        <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
      </div>
    );
  };

  const renderCell = (item, columnKey) => {
    const cellValue = item[columnKey];

    switch (columnKey) {
      case "operator_name":
        return (
          <div className="py-[16px]">
            <ACircleUserDisplay
              firstname={capitalizeFirstLetter(item.first_name)}
              lastname={capitalizeFirstLetter(item.last_name)}
              email={item.email}
            />
          </div>
        );
      case "average_grade":
        return (
          <div className="w-full flex justify-start items-center">
            <AStarGradeDisplay grade={item?.average_grade} />
          </div>
        );
      case "team_grade":
        return (
          <div className="w-full flex justify-start items-center">
            <AStarGradeDisplay grade={item?.team_grade} />
          </div>
        );
      case "actions":
        return (
          <div className="w-full h-full flex items-center justify-end pr-[8px]">
            <TfiArrowTopRight
              size={20}
              color="#737373"
            />
          </div>
        );
      default:
        return <span className="text-sm text-[#404040]/[0.6]">{cellValue}</span>;
    }
  };

  if (loading_operators) {
    // TODO: render loading
    // return <Loader />;
  }

  return (
    <Card
      className="w-full h-full rounded-md shadow-custom-light px-[12px] py-[20px]"
      shadow="none"
    >
      <CardHeader className="py-0 px-[20px]">{renderCardHeader()}</CardHeader>
      <CardBody className="w-full h-full min-h-[300px] p-0 pb-3 box-border">
        {loading_operators ? (
          <div className="flex flex-col justify-center items-center w-auto h-96 m-auto text-sm">
            <CircularProgress
              aria-label={t("Loading...")}
              color="primary"
              label={t("Loading...")}
            />
          </div>
        ) : topFlopCalls?.length > 0 ? (
          <GenericTable
            items={topFlopCalls}
            titles={dashboard_table_columns}
            getItemContent={renderCell}
            onRowClick={onOperatorClick}
          />
        ) : (
          tableEmptyContent()
        )}
      </CardBody>
    </Card>
  );
};

export default TopFlopTable;
